import { CellClassParams, ColDef, ICellRendererParams, ValueParserParams, ValueSetterParams } from 'ag-grid-community';
import {
  GetEntityId,
  GridMode,
  RowWithMetadata,
  SetEditedRow,
  SetRowDataFn,
  SetRowDataParams,
} from './useEditableGridLogic';
import SelectCellEditor, { baseCellEditorParams } from './selectCellEditor.component';
import { withIndicatorAndCustomInnerRendererCellRenderer } from './validationMessagesIndicatorCellRenderer';
import { adminColumnSharedFields } from '../columnDefinitions';
import { getQuickFilterTextWithSkippingAddedRow } from './quickFilterUtils';
import { ColumnDefinition } from '@common/xlsxExport/types';

export type BaseContext = {
  editedRowId?: string,
  mode: GridMode,
}

export type ValueSetterFactory<TData, TLookupData> = ({ lookUpData, setRowData }: {
  lookUpData: TLookupData,
  setRowData: SetRowDataFn<TData>
}) => (params: Omit<ValueSetterParams, 'data'> & {data: TData}) => void;

export type AdditionalConditionParams<
  TData,
  TLookupData,
  TContext extends BaseContext = BaseContext
  > = {
  data: RowWithMetadata<TData>, lookUpData: TLookupData, context: TContext
}

export type AdditionalCondition<
  TData,
  TLookupData,
  TContext extends BaseContext = BaseContext
  > = (params: AdditionalConditionParams<TData, TLookupData, TContext> ) => boolean;

export type EditableWhenConfig<
  TData extends RowWithMetadata<unknown>,
  TContext extends BaseContext = any,
  TLookupData extends unknown = any
  > = {
  /** callback that specifies if given field is editable in grid */
  additionalCondition?: AdditionalCondition<TData, TLookupData, TContext>,
  /** ag-grid property that allows parsing edited cell value */
  valueParser?: (params: ValueParserParams) => any,
  /** factory function that allows overriding default valueSetter behaviour, by default valueSetter is calling setRowData callback from useEditableGridLogic   */
  valueSetterFactory?: ValueSetterFactory<TData, TLookupData>,
  /** ag-grid property that allows to override column definition field attribute, and provide custom getter instead  */
  valueGetter?: ColDef['valueGetter'],
  /** ag-grid property that allows complex cell rendering using react component  */
  cellRenderer?: ColDef['cellRenderer']
}

export type ColumnDefinitionConfig<TData, TLookUpData> = {
  /** base column definitions that will be decorated with additional properties that allows editable grid behaviour */
  baseColumnDefinition: ColumnDefinition & {[key: string]: any},
  /** list of GridModes for which the field will be editable  */
  editableWhen?: GridMode[],
  /** allows adding additional configuration when grid is editable  */
  editableWhenConfig?: EditableWhenConfig<RowWithMetadata<TData>, any, TLookUpData>,
  /** option that changes cell editor to dropdown  */
  withDropdown?: boolean,
  /** additional options for dropdown cell editor */
  withDropdownConfig?: WithDropdownConfig<TLookUpData>,
}

export const defaultColDefProperties = {
  cellClassRules: {
    'disable-editing': () => true,
  },
  filterParams: { newRowsAction: 'keep' },
};


type AddEditablePropertiesFactoryParams<
  TData extends RowWithMetadata<unknown>,
  TContext extends BaseContext,
  TLookupData extends unknown
> = {
  /** base column definitions that will be decorated with additional properties that allows editable grid behaviour */
  baseColumnDefinition: ColDef,
  /** callback from useEditableGridLogic helper function */
  setRowData: SetRowDataFn<TData>,
  /** list of GridModes for which the field will be editable  */
  editableWhen: GridMode[],
  /** allows adding additional configuration when grid is editable  */
  editableWhenConfig?: EditableWhenConfig<TData, TContext, TLookupData>,
  /** additional context that can be used in various factory callbacks:
   * - for providing options for dropdowns in conjuntion with optionGetter
   * - as context for row comparison when checking if row is editable (additionalCondition callback)
   * - as context for valueSetterFactory, can be used to set edited row values based on this data
  */
  lookUpData: TLookupData,
  /** function that returns unique id for each rows, used for comparing grid data rows */
  getEntityId: GetEntityId<TData>,
}

export const addEditablePropertiesFactory = <
  TData extends RowWithMetadata<unknown>,
  TContext extends BaseContext,
  TLookupData extends unknown
  >({
    baseColumnDefinition,
    editableWhen,
    setRowData,
    editableWhenConfig: {
      additionalCondition,
      valueParser,
      valueSetterFactory,
      valueGetter,
      cellRenderer,
    } = {},
    lookUpData,
    getEntityId,
  }: AddEditablePropertiesFactoryParams<TData, TContext, TLookupData>) => {
  const editable = ({ data, context }: { data: RowWithMetadata<TData>, context: TContext }) => {
    if (!context || !data) {
      return false;
    }

    return editableWhen.some(allowed => allowed === context.mode)
      && (
        (context.mode === GridMode.Edit && context.editedRowId === getEntityId(data))
        || context.mode === GridMode.EditAll
        || (context.mode === GridMode.Add && data.meta.isAdded)
      )
      && (additionalCondition || (() => true))({ data, lookUpData, context });
  };
  return ({
    ...baseColumnDefinition,
    editable,
    cellClassRules: {
      ...baseColumnDefinition.cellClassRules ?? {},
      'disable-editing': (args: CellClassParams) => !editable(args),
    },
    valueSetter: valueSetterFactory
      ? valueSetterFactory({ lookUpData, setRowData })
      : (params: SetRowDataParams<TData>) => setRowData(params),
    filterParams: { newRowsAction: 'keep' },
    valueParser,
    valueGetter,
    ...(cellRenderer ? { cellRenderer } : {}),
  });
};


export type WithDropdownConfig<TLookUpData extends unknown = any> = {
  /** callback that allows specifing what data will be used as options in dropdown */
  optionsGetter?: (context: TLookUpData, rowParams: any) => any,
  /** params that will be passed into cellRenderer component */
  cellEditorParams?: any
}

export const addWithDropdownProperties = <TLookUpData extends unknown = any>(
  columnDefinition: ColDef,
  config: WithDropdownConfig<TLookUpData> = {},
  lookUpData: TLookUpData
) => ({
    ...columnDefinition,
    cellEditor: SelectCellEditor,
    cellEditorPopup: true,
    cellEditorParams: (rowParams: any) => ({
      options: config.optionsGetter ? config.optionsGetter(lookUpData, rowParams) : [],
      ...baseCellEditorParams,
      ...config.cellEditorParams ? config.cellEditorParams : {},
    }),
  });


type AdminColumnDefinitionFactoryParams<TData, TContext> = {
  /** callback from useEditableGridLogic helper function */
  editRow: SetEditedRow<TData>
  /** ag-grid property that allows complex cell rendering using react component  */
  cellRenderer: ColDef['cellRenderer'],
  /** additional context passed into cellRendererParams callback */
  context?: TContext,
  /** callback that returns params that will be passed into cellRenderer component */
  cellRendererParams?: (params: ICellRendererParams) => Record<string, unknown>
}

export const adminColumnDefinitionFactory = <TData, TContext>({
  editRow,
  cellRenderer,
  context,
  cellRendererParams,
}: AdminColumnDefinitionFactoryParams<TData, TContext>) => ({
    ...adminColumnSharedFields,
    ...defaultColDefProperties,
    cellRenderer,
    cellRendererParams: (params: ICellRendererParams) => ({
      editRow,
      context,
      ...cellRendererParams?.(params) ?? {},
    }),
    valueGetter: () => '',
    getQuickFilterText: getQuickFilterTextWithSkippingAddedRow,
    minWidth: 80,
  });


type BuildEditableGridColumnDefinitionsParams<TData, TLookUpData> = {
  /** base column definitions that will be decorated with additional properties that allows editable grid behaviour */
  baseColumnDefinitions: ColumnDefinitionConfig<TData, TLookUpData>[],
  /** callback from useEditableGridLogic helper function */
  setRowData: SetRowDataFn<RowWithMetadata<TData>>,
  /** additional context that can be used in various factory callbacks:
   * - for providing options for dropdowns in conjuntion with optionGetter
   * - as context for row comparison when checking if row is editable (additionalCondition callback)
   * - as context for valueSetterFactory, can be used to set edited row values based on this data
  */
  lookUpData: TLookUpData,
  /** function that returns unique id for each rows, used for comparing grid data rows */
  getEntityId: GetEntityId<TData>,
}

export const buildEditableGridColumnDefinitions = <TData, TLookUpData>({
  setRowData,
  lookUpData,
  baseColumnDefinitions,
  getEntityId,
}: BuildEditableGridColumnDefinitionsParams<TData, TLookUpData>): ColDef[] => baseColumnDefinitions.map(({
    baseColumnDefinition,
    editableWhen,
    editableWhenConfig,
    withDropdown,
    withDropdownConfig,
  }) => withIndicatorAndCustomInnerRendererCellRenderer({
    ...baseColumnDefinition,
    ...defaultColDefProperties,
    ...editableWhen
      ? addEditablePropertiesFactory({
        baseColumnDefinition,
        editableWhen,
        editableWhenConfig,
        setRowData,
        getEntityId,
        lookUpData,
      })
      : {},
    ...withDropdown
      ? addWithDropdownProperties(baseColumnDefinition, withDropdownConfig, lookUpData)
      : {},
  }));
